import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useIsMobile from '../../hooks/useIsMobile';
import './InvAc.css';
import { findSeqIndex } from '../../utils/seqDataUtils';

/**
 * 전체 인버터 AC 통합 전압/전류 표시
 * - Redux의 ivData/Iv_list에서 값을 읽어와 계산
 * - 스케일: 전압/전류 모두 0.1 단위로 전달되므로 /10 해서 표시
 * - 전압: 각 인버터 대표전압(우선순위 RS→ST→TR)을 수집해 평균
 * - 전류: 각 인버터 대표전류(우선순위 R→S→T)를 모두 합산
 */
function InvAc() {
  const isMobile = useIsMobile();
  const ivData = useSelector((state) => state.ivData || {});
  const ivList = useSelector((state) => state.Iv_list || {});

  const [totalVolt, setTotalVolt] = useState(null);
  const [totalCurr, setTotalCurr] = useState(null);

  const seqArray = useMemo(() => (Array.isArray(ivList.seq) ? ivList.seq : []), [ivList.seq]);

  useEffect(() => {
    if (!Array.isArray(ivData.seq)) return;

    const voltSamples = [];
    let currSum = 0;

    seqArray.forEach((seq) => {
      const dataIndex = findSeqIndex(ivData, seq);
      if (dataIndex === -1) return;

      const pickVoltage = () => {
        const candidates = [
          ivData.Grid_RS_Volt?.[dataIndex],
          ivData.Grid_ST_Volt?.[dataIndex],
          ivData.Grid_TR_Volt?.[dataIndex],
        ];
        for (const v of candidates) {
          const num = Number(v);
          if (Number.isFinite(num) && num > 0) return num;
        }
        return 0;
      };

      const pickCurrent = () => {
        const candidates = [
          ivData.Grid_R_Current?.[dataIndex],
          ivData.Grid_S_Current?.[dataIndex],
          ivData.Grid_T_Current?.[dataIndex],
        ];
        for (const c of candidates) {
          const num = Number(c);
          if (Number.isFinite(num) && num !== 0) return num;
        }
        return 0;
      };

      const v = pickVoltage();
      if (v > 0) voltSamples.push(v);
      currSum += pickCurrent();
    });

    const avgVoltRaw =
      voltSamples.length > 0 ? voltSamples.reduce((a, b) => a + b, 0) / voltSamples.length : 0;
    // 스케일링(0.1 단위)
    setTotalVolt(Number.isFinite(avgVoltRaw) ? (avgVoltRaw / 10).toFixed(1) : null);
    setTotalCurr(Number.isFinite(currSum) ? (currSum / 10).toFixed(1) : null);
  }, [
    ivData.Grid_RS_Volt,
    ivData.Grid_ST_Volt,
    ivData.Grid_TR_Volt,
    ivData.Grid_R_Current,
    ivData.Grid_S_Current,
    ivData.Grid_T_Current,
    ivData.seq,
    seqArray,
  ]);

  const displayVolt = totalVolt ?? '정보없음';
  const displayCurr = totalCurr ?? '정보없음';

  return (
    <div className="invac-wrap">
      <div className="inverterGenInfo-TopBox">
        <div
          className="invac-title"
          style={{ fontSize: isMobile ? '17px' : '20px' }}
        >
          ◎ 전체 인버터 AC 정보
        </div>
        <div style={{ flexGrow: '1' }}></div>
        <div
          className="invac-menu"
          style={{ fontSize: isMobile ? '17px' : '20px' }}
        >
          ⦁ ⦁ ⦁
        </div>
      </div>

      <div className="inverterGenInfo-BottomBox">
        <div className="invac-cards">
          <div className="invac-card">
            <div className="invac-label">통합 전압</div>
            <div className="invac-value">
              <span className="invac-number">{displayVolt}</span>
              {displayVolt !== '정보없음' && <span className="invac-unit">V</span>}
            </div>
          </div>

          <div className="invac-card">
            <div className="invac-label">통합 전류</div>
            <div className="invac-value">
              <span className="invac-number">{displayCurr}</span>
              {displayCurr !== '정보없음' && <span className="invac-unit">A</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvAc;
